import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import luxonPlugin from "@fullcalendar/luxon3";
import { DateTime } from "luxon";
import { RootState } from "typedefs";
import { CalendarPreferences } from "app/models/CalendarPreferences";
import { setSelectedEvent, fetchEvents, clearEvents } from "app/actions/events";
import i18n from "app/i18n";
import { formatMonthDay } from "app/utils/luxonFormats";

import MonthEvent from "../../Event/MonthEvent";
import MonthSkeleton from "./monthSkeleton";
import styles from "./styles.module.scss";

interface MonthViewProps {
  calendarRef: React.RefObject<any>;
  timeZone: string;
  preferences: CalendarPreferences;
  eventElRef: React.RefObject<any>;
  initialDate: DateTime;
  onIntervalChange: (start: DateTime, end: DateTime) => void;
  onDateClick: (date: DateTime) => void;
  businessHours?: any[];
}

export default function MonthView({
  calendarRef,
  timeZone,
  preferences,
  eventElRef,
  initialDate,
  onIntervalChange,
  onDateClick,
  businessHours,
}: MonthViewProps) {
  const dispatch = useDispatch();
  const events = useSelector((state: RootState) => state.events);
  const initialLoadDoneRef = useRef(false);
  const [, setForceUpdate] = useState(0);

  const availableDates = useMemo(() => {
    if (!businessHours || businessHours.length === 0) {
      return null;
    }
    const dates = new Set<string>();
    businessHours.forEach((bh: any) => {
      if (bh.date && bh.startTime !== bh.endTime) {
        const iso =
          typeof bh.date === "string"
            ? bh.date
            : bh.date.toISODate
              ? bh.date.toISODate()
              : DateTime.fromJSDate(bh.date).toISODate();
        if (iso) {
          dates.add(iso);
        }
      }
    });
    return dates;
  }, [businessHours]);

  const availableDatesRef = useRef(availableDates);
  if (availableDates) {
    availableDatesRef.current = availableDates;
  }

  // FullCalendar hands cell dates as real instants; read them in the
  // calendar's time zone, not the browser's, or every label/lookup shifts a
  // day when the two zones disagree (CN-2586).
  const dayCellClassNames = useCallback(
    (arg: any) => {
      const dates = availableDatesRef.current;
      if (!dates) {
        return [];
      }
      const iso = DateTime.fromJSDate(arg.date).setZone(timeZone).toISODate();
      return dates.has(iso) ? [] : ["fc-day-unavailable"];
    },
    [timeZone],
  );

  const dayCellDidMount = useCallback(
    (arg: any) => {
      const dates = availableDatesRef.current;
      if (!dates) {
        return;
      }
      const iso = DateTime.fromJSDate(arg.date).setZone(timeZone).toISODate();
      if (!dates.has(iso)) {
        arg.el.classList.add("fc-day-unavailable");
      } else {
        arg.el.classList.remove("fc-day-unavailable");
      }
    },
    [timeZone],
  );

  useEffect(() => {
    if (!availableDates) {
      return;
    }
    const cells = document.querySelectorAll(".fc-daygrid-day[data-date]");
    cells.forEach((cell) => {
      const dateStr = cell.getAttribute("data-date");
      if (dateStr) {
        if (availableDates.has(dateStr)) {
          cell.classList.remove("fc-day-unavailable");
        } else {
          cell.classList.add("fc-day-unavailable");
        }
      }
    });
  }, [availableDates]);

  useEffect(() => {
    dispatch(clearEvents());
  }, []);

  const renderEvent = useCallback(
    (info: any) => <MonthEvent {...info} timeZone={timeZone} />,
    [timeZone],
  );

  const handleEventClick = useCallback(
    (info: any) => {
      (eventElRef as React.MutableRefObject<any>).current = info.el;
      dispatch(setSelectedEvent(parseInt(info.event.id, 10)));
    },
    [dispatch, eventElRef],
  );

  const handleDateClick = useCallback(
    (info: any) => {
      const clickedDate = DateTime.fromJSDate(info.date).setZone(timeZone);
      onDateClick(clickedDate);
    },
    [timeZone, onDateClick],
  );

  const dayCellContent = useCallback(
    (arg: any) => {
      const cellDate = DateTime.fromJSDate(arg.date).setZone(timeZone);
      if (cellDate.day === 1) {
        return formatMonthDay(cellDate, preferences.dateFormat);
      }
      return String(cellDate.day);
    },
    [timeZone, preferences.dateFormat],
  );

  const handleDatesSet = useCallback(
    (dateInfo: any) => {
      const start = DateTime.fromJSDate(dateInfo.start).setZone(timeZone);
      const end = DateTime.fromJSDate(dateInfo.end).setZone(timeZone);
      onIntervalChange(start, end);
      const promise = dispatch(
        fetchEvents({ start: start.toISO(), end: end.toISO() }) as any,
      );
      if (!initialLoadDoneRef.current) {
        Promise.resolve(promise).then(() => {
          initialLoadDoneRef.current = true;
          setForceUpdate((n) => n + 1);
        });
      }
    },
    [timeZone, onIntervalChange, dispatch],
  );

  const showSkeleton = !initialLoadDoneRef.current;

  return (
    <div className={styles.month_container}>
      {showSkeleton && (
        <MonthSkeleton
          initialDate={initialDate}
          firstDay={preferences.firstDay}
          dateFormat={preferences.dateFormat}
          timeZone={timeZone}
        />
      )}
      <div
        style={
          showSkeleton
            ? { visibility: "hidden", height: 0, overflow: "hidden" }
            : { height: "100%" }
        }
      >
        <FullCalendar
          headerToolbar={false}
          ref={calendarRef}
          events={events.data as any[]}
          locale={i18n.language}
          plugins={[dayGridPlugin, luxonPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          initialDate={initialDate?.toJSDate()}
          timeZone={timeZone}
          height="100%"
          fixedWeekCount={false}
          eventBackgroundColor="#039BE5"
          firstDay={preferences.firstDay}
          selectable={false}
          eventTimeFormat={{
            hour: "numeric" as const,
            minute: "2-digit" as const,
            omitZeroMinute: true,
            hour12: preferences.timeFormat === "12h",
          }}
          eventContent={renderEvent}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          datesSet={handleDatesSet}
          dayCellClassNames={dayCellClassNames}
          dayCellDidMount={dayCellDidMount}
          dayCellContent={dayCellContent}
        />
      </div>
    </div>
  );
}
